import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { AddressDTO, AdvanceState, AggregatedEmailStatusCode, CalculationMethod, DdkState, EmailDeliveryStatus, EppState, FiscalizationState, InventoryDocumentKind, InventoryEffect, JpkVatState, KsefDocumentStatus, KsefProcessingStage, KsefSendSubmissionMode, OrderState, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Transform, Type } from 'class-transformer';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from '../../common/query/paged-collection-query';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { CurrencyCollectionQuery, CurrencyItem } from '../../currency/queries/currency.query-base';
import { IKsefDeadlineDateCalculator, isAdjustment } from '../utils/sale-utils';

export interface SaleGridItem {
    version: string;
    id: number;
    externalId: string;

    kind: SaleDocumentKind;
    documentNumber: string;
    issuer: string;
    clientId?: number;
    clientName: string;
    clientTin: string;
    clientEmails: string[];
    clientCountry: string;
    clientAddress: string;

    dueDate: Date;
    taxLiabilityDate: Date;
    issueDate: Date;
    orderDate: Date;
    deliveryDate?: Date;
    saleDate?: Date;

    adjustment: boolean;
    documentInForeginCurrency: boolean;

    favourite: boolean;

    aggregatedEmailStatusCode: AggregatedEmailStatusCode;
    emailStatuses: EmailDeliveryStatus[];
    orderState: OrderState;
    fiscalizationState: FiscalizationState;
    jpkVatState: JpkVatState;
    ddkState: DdkState;
    eppState: EppState;

    calculationMethod: CalculationMethod;

    advanceValue?: number;
    advanceValueBaseCurrency?: number;
    finalAdvance?: boolean;
    advanceState?: AdvanceState;
    firstAdvanceNumber?: string;
    advanceWithAdjustment?: boolean;
    advanceWithChangedVatRate?: boolean;
    isFinalAdvanceInChain?: boolean;

    baseCurrency: CurrencyItem;
    baseGrossValue: number;
    baseNetValue: number;

    currency: CurrencyItem;
    grossValue: number;
    netValue: number;
    leftToPay: number;

    ksefProcessingStage: KsefProcessingStage;
    ksefDocumentStatus: KsefDocumentStatus;
    ksefDocumentId: string;
    ksefCollectiveIdentifier: string;
    ksefSubmissionMode: KsefSendSubmissionMode;
    ksefXmlExported: boolean | null;
    ksefXmlImported: boolean | null;
    relatedInventoryGoodsIssueDocument: {
        documentId: number;
        kind: InventoryDocumentKind,
        number: string;
    };
    inventoryEffect: InventoryEffect;
    invoiceToReceipt: boolean;
    note: string | null;
    ksefShippingDeadlineDate: Date | null;
}

class SaleInternalGridItemDTO {
    public readonly advanceWithAdjustment!: boolean;
    public readonly advanceWithChangedVatRate!: boolean;
    public readonly isFinalAdvanceInChain!: boolean;
    public readonly advanceValue!: number;
    public readonly advanceValueBaseCurrency!: number;
    public readonly buyer!: string;
    public readonly calculationMethod!: CalculationMethod;
    public readonly client!: string;
    public readonly clientId?: number;
    public readonly clientTin!: string;
    @Type(() => AddressDTO) public readonly clientAddress!: AddressDTO;
    public readonly clientEmails!: string[];
    public readonly currencyId!: number;
    public readonly ddkState!: DdkState;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly deliveryDate!: Date;
    public readonly documentNumber!: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    public readonly aggregatedEmailStatusCode!: AggregatedEmailStatusCode;
    @Type(() => EmailDeliveryStatus) public readonly emailStatuses!: EmailDeliveryStatus[];
    public readonly eppState!: EppState;
    public readonly favourite!: boolean;
    public readonly finalAdvance!: boolean;
    public readonly firstAdvanceNumber!: string;
    public readonly advanceState!: AdvanceState;
    public readonly fiscalizationState!: FiscalizationState;
    public readonly id!: number;
    public readonly externalId!: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    public readonly issuer!: string;
    public readonly jpk_vatState!: JpkVatState;
    public readonly kind!: SaleDocumentKind;
    public readonly leftToPay!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly orderDate!: Date;
    public readonly orderState!: OrderState;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly taxLiabilityDate!: Date;
    public readonly totalBaseCurrencyGrossValue!: number;
    public readonly totalBaseCurrencyNetValue!: number;
    public readonly totalGrossValue!: number;
    public readonly totalNetValue!: number;
    public readonly transactionLocation!: string;
    public readonly ksefProcessingStage!: KsefProcessingStage;
    public readonly ksefDocumentStatus!: KsefDocumentStatus;
    public readonly ksefDocumentId!: string;
    public readonly ksefCollectiveIdentifier: string;
    public readonly ksefSubmissionMode!: KsefSendSubmissionMode;
    public readonly ksefXmlExported!: boolean | null;
    public readonly ksefXmlImported!: boolean | null;
    public readonly version!: string;
    public readonly relatedInventoryGoodsIssueDocument!: {
        readonly documentId: number;
        readonly kind: InventoryDocumentKind,
        readonly number: string;
    };
    public readonly inventoryEffect!: InventoryEffect;
    public readonly invoiceToReceipt!: boolean;
    public readonly note: string | null;
}

export class SaleGridQuery extends PagedCollectionQuery<SaleGridItem> { }

export class SaleGridQueryHandlerBase implements QueryHandler<SaleGridQuery, PagedCollectionQueryResult<SaleGridItem>> {
    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase,
        private readonly ksefDeadlineCalculator: IKsefDeadlineDateCalculator
    ) { }

    public async handleAsync(query: SaleGridQuery, kind: SaleDocumentKind): Promise<PagedCollectionQueryResult<SaleGridItem>> {
        const url = this.getUrlByKind(kind);
        const { data } = await this.http.get(url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<SaleInternalGridItemDTO>(SaleInternalGridItemDTO)
        });

        const items = await Promise.all(data.items.map<Promise<SaleGridItem>>(async item => {
            const itemCurrency = await this.rootContext.firstAsync(CurrencyCollectionQuery, { id: item.currencyId });
            const { systemCurrency } = await this.rootContext.getAsync(CompanyParametersQuery);
            const baseCurrency = await this.rootContext.firstAsync(CurrencyCollectionQuery, { symbol: systemCurrency });
            const country = await this.rootContext.firstAsync(CountryCollectionQuery, {id: item.clientAddress?.countryId});

            return {
                version: item.version,
                id: item.id,
                externalId: item.externalId,

                note: item.note,

                kind: item.kind,
                documentNumber: item.documentNumber,
                issuer: item.issuer,

                clientId: item.clientId,
                clientAddress: item.clientAddress ? [item.clientAddress.address, item.clientAddress.city].filter(Boolean).join(', ') : '',
                clientEmails: item.clientEmails,
                clientName: item.client,
                clientTin: item.clientTin,
                clientCountry: country?.name || '',

                dueDate: item.dueDate,
                taxLiabilityDate: item.taxLiabilityDate,
                deliveryDate: item.deliveryDate,
                issueDate: item.issueDate,
                orderDate: item.orderDate,

                adjustment: isAdjustment(item.kind),
                documentInForeginCurrency: itemCurrency!.symbol !== baseCurrency!.symbol,

                favourite: item.favourite,
                finalAdvance: item.finalAdvance,
                firstAdvanceNumber: item.firstAdvanceNumber,
                advanceState: item.advanceState,
                aggregatedEmailStatusCode: item.aggregatedEmailStatusCode,
                emailStatuses: item.emailStatuses.map(s => ({
                    recipient: s.recipient,
                    status: s.status,
                    description: s.description,
                    updatedAt: s.updatedAt
                })),
                orderState: item.orderState,
                fiscalizationState: item.fiscalizationState,
                jpkVatState: item.jpk_vatState,
                ddkState: item.ddkState,
                eppState: item.eppState,

                calculationMethod: item.calculationMethod,

                advanceValue: item.advanceValue,
                advanceValueBaseCurrency: item.advanceValueBaseCurrency,

                baseCurrency: baseCurrency!,
                baseGrossValue: item.totalBaseCurrencyGrossValue,
                baseNetValue: item.totalBaseCurrencyNetValue,

                currency: itemCurrency!,
                grossValue: item.totalGrossValue,
                netValue: item.totalNetValue,
                leftToPay: item.leftToPay,
                advanceWithAdjustment: item.advanceWithAdjustment,
                advanceWithChangedVatRate: item.advanceWithChangedVatRate,
                isFinalAdvanceInChain: item.isFinalAdvanceInChain,

                ksefDocumentId: item.ksefDocumentId,
                ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
                ksefSubmissionMode: item.ksefSubmissionMode,
                ksefDocumentStatus: item.ksefDocumentStatus,
                ksefProcessingStage: item.ksefProcessingStage,
                ksefXmlExported: item.ksefXmlExported,
                ksefXmlImported: item.ksefXmlImported,
                relatedInventoryGoodsIssueDocument: item.relatedInventoryGoodsIssueDocument,
                invoiceToReceipt: item.invoiceToReceipt,
                inventoryEffect: item.inventoryEffect,
                ksefShippingDeadlineDate: this.ksefDeadlineCalculator.calculate(item.ksefSubmissionMode, item.issueDate)
            };
        }));

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }

    private getUrlByKind(kind: SaleDocumentKind): string {
        const core = TaggedUrl.join('sales', [Tags.Api]);
        const params = ({
            [SaleDocumentKind.Invoice]: '?kind=invoice&kind=invoiceAdjustment',
            [SaleDocumentKind.Receipt]: '?kind=receipt&kind=receiptAdjustment',
            [SaleDocumentKind.VatMarginInvoice]: '?kind=vatMarginInvoice&kind=vatMarginInvoiceAdjustment',
            [SaleDocumentKind.ProForma]: '?kind=proForma',
            [SaleDocumentKind.AdvanceInvoice]: '?kind=advanceInvoice&kind=advanceInvoiceAdjustment',
        } as Record<any, string>)[kind] || '';
        return core + params;
    }
}
